// packages/utils/src/glow-score.ts
// FRAME v2 — Glow Score (Optimistic Potential Score)
//
// Answers: "What could this photo score if fixable things were fixed?"
//
// Strategy:
// 1. Remove all detractor penalties (they're fixable — retake the photo)
// 2. Lift weakest dimension to subject's demonstrated capability
//    (2nd-highest dimension = conservative real potential)
// 3. Compute weighted composite with mode weights
// 4. Transform to FRAME scale
// 5. Cap: glow <= actual + 2.0 and <= 9.8

import type { WeightProfile } from '@frame/scoring-types';
import { transformScore } from './score-transform';
import { normalizeWeights } from './scoring';

export interface GlowResult {
  glowScore: number;
  delta: number;
  explanation: string;
  weakestDimension: string;
  weakestLabel: string;
}

const DIM_LABELS: Record<string, string> = {
  portrait: 'portrait composition',
  lighting: 'lighting',
  composition: 'composition',
  scene: 'scene interest',
};

const DIM_SUGGESTIONS: Record<string, string> = {
  portrait: 'a more natural expression or flattering angle',
  lighting: 'better lighting (try golden hour or window light)',
  composition: 'tighter composition (focus on eyes, rule of thirds)',
  scene: 'a cleaner or more interesting background',
};

/**
 * Compute the glow score from raw dimension scores.
 */
export function computeGlowScore(
  rawDimScores: { portrait: number | null; lighting: number; composition: number; scene: number },
  rawComposite: number,
  totalDetractorPenalty: number,
  actualFrameScore: number,
  weights: WeightProfile,
): GlowResult {
  const dims: { key: string; score: number }[] = [];
  if (rawDimScores.portrait !== null) {
    dims.push({ key: 'portrait', score: rawDimScores.portrait });
  }
  dims.push({ key: 'lighting', score: rawDimScores.lighting });
  dims.push({ key: 'composition', score: rawDimScores.composition });
  dims.push({ key: 'scene', score: rawDimScores.scene });

  if (dims.length === 0) {
    return { glowScore: actualFrameScore, delta: 0, explanation: '', weakestDimension: '', weakestLabel: '' };
  }

  dims.sort((a, b) => a.score - b.score);
  const weakest = dims[0];
  const secondBest = dims.length >= 3 ? dims[dims.length - 2] : dims[dims.length - 1];

  const liftedDims: Record<string, number> = {};
  for (const d of dims) {
    liftedDims[d.key] = d.key === weakest.key ? Math.max(d.score, secondBest.score) : d.score;
  }

  const norm = normalizeWeights(weights);
  let glowRawComposite = 0;
  let totalWeight = 0;

  if (liftedDims.portrait !== undefined) {
    glowRawComposite += liftedDims.portrait * norm.portraitWeight;
    totalWeight += norm.portraitWeight;
  }
  glowRawComposite += liftedDims.lighting * norm.lightingWeight;
  totalWeight += norm.lightingWeight;
  glowRawComposite += liftedDims.composition * norm.compositionWeight;
  totalWeight += norm.compositionWeight;
  glowRawComposite += liftedDims.scene * norm.sceneWeight;
  totalWeight += norm.sceneWeight;

  if (totalWeight > 0) glowRawComposite /= totalWeight;

  let glowTransformed = transformScore(glowRawComposite);

  glowTransformed = Math.min(glowTransformed, actualFrameScore + 2.0, 9.8);
  glowTransformed = Math.max(glowTransformed, actualFrameScore);
  glowTransformed = Math.round(glowTransformed * 10) / 10;

  const delta = Math.round((glowTransformed - actualFrameScore) * 10) / 10;

  let explanation: string;
  if (delta <= 0.2) {
    explanation = 'This photo is already near its potential — great shot!';
  } else {
    const parts: string[] = [];
    if (totalDetractorPenalty > 0.5) parts.push('removing detractors');
    parts.push(DIM_SUGGESTIONS[weakest.key] ?? `improving ${DIM_LABELS[weakest.key]}`);
    explanation = `Could reach ${glowTransformed.toFixed(1)} with ${parts.join(' and ')}`;
  }

  return {
    glowScore: glowTransformed,
    delta,
    explanation,
    weakestDimension: weakest.key,
    weakestLabel: DIM_LABELS[weakest.key] ?? weakest.key,
  };
}
